/**
 * GuidedTour — el motor del tutorial guiado.
 *
 * Qué hace:
 *   1. Lleva al usuario a la pantalla del paso (si el paso declara `route`).
 *   2. Busca el elemento a resaltar y le abre un foco (agujero en el velo) que
 *      sigue al elemento aunque la página haga scroll o cambie de tamaño.
 *   3. Pinta una burbuja a su lado con la explicación, el progreso y las notas
 *      de profesor.
 *   4. Comprueba cada medio segundo si el usuario ya ha hecho la acción
 *      (`detect`) y, si el paso es automático, avanza solo.
 *
 * El velo NO captura el ratón: se puede escribir y pulsar en la aplicación con
 * el tutorial abierto. Si bloquease los clics sería un vídeo, no un tutorial.
 *
 * Para los pasos que cuentan algo que ya existía antes (nº de flechas en el
 * lienzo del Diseñador, por ejemplo), el paso declara `measure`: el motor toma
 * la medida cuando la pantalla ya está montada y estable, y la usa como
 * referencia para distinguir lo nuevo.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTutorial } from './TutorialContext';
import { createCtx, findTarget } from './dom';
import { performStep, goViaMenu, sleep } from './actions';
import api from '../services/api';
import './GuidedTour.css';

const SPOT_PAD = 8;
const BUBBLE_W = 380;
const BUBBLE_H = 250;     // estimación de alto, para no taparlo que se resalta
const BUBBLE_GAP = 18;
const POLL_MS = 500;
const STABLE_MS = 1200;   // el lienzo del Diseñador tarda en montar: se espera

const clamp = (v, min, max) => Math.max(min, Math.min(v, max));

function overlaps(a, b) {
  return !(a.left + a.width < b.left || b.left + b.width < a.left ||
           a.top + a.height < b.top || b.top + b.height < a.top);
}

/**
 * Coloca la burbuja sin tapar el elemento resaltado: si la posición preferida
 * lo pisa (y eso impediría pulsarlo), se prueba la siguiente. Es lo que evita
 * el fallo más típico de un tutorial: la ventana de instrucciones encima del
 * botón que te dice que pulses.
 */
function placeBubble(rect, preferred) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Lo resaltado es demasiado grande para tener nada al lado: esquina lejana.
  if (rect.height > vh * 0.45 || rect.width > vw * 0.75) {
    return rect.top > vh / 2
      ? { left: 16, top: 92 }
      : { left: vw - BUBBLE_W - 16, top: vh - BUBBLE_H - 16 };
  }

  const target = { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
  const candidates = [];
  const push = (left, top) => candidates.push({ left: clamp(left, 12, vw - BUBBLE_W - 12), top: clamp(top, 76, vh - BUBBLE_H - 12) });

  for (const p of [preferred, 'right', 'left', 'bottom', 'top']) {
    if (!p) continue;
    if (p === 'right') push(rect.right + BUBBLE_GAP, rect.top);
    else if (p === 'left') push(rect.left - BUBBLE_W - BUBBLE_GAP, rect.top);
    else if (p === 'bottom') push(rect.left, rect.bottom + BUBBLE_GAP);
    else if (p === 'top') push(rect.left, rect.top - BUBBLE_H - BUBBLE_GAP);
  }

  const free = candidates.find(c => !overlaps({ ...c, width: BUBBLE_W, height: BUBBLE_H }, target));
  if (free) return free;
  return rect.top > vh / 2 ? { left: 16, top: 92 } : { left: vw - BUBBLE_W - 16, top: vh - BUBBLE_H - 16 };
}

/** Renderiza el texto del paso: párrafos, listas con «· », **negrita** y `código`. */
function inline(text) {
  const parts = String(text).split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('`') && part.endsWith('`')) return <code key={i} className="gt-code">{part.slice(1, -1)}</code>;
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function RichText({ text }) {
  const blocks = String(text || '').split('\n\n');
  return (
    <>
      {blocks.map((block, bi) => {
        const lines = block.split('\n');
        if (lines.length > 1 && lines.every(l => l.trim().startsWith('·') || /^\d\./.test(l.trim()))) {
          return (
            <ul key={bi} className="gt-list">
              {lines.map((l, li) => <li key={li}>{inline(l.replace(/^\s*[·]\s*/, ''))}</li>)}
            </ul>
          );
        }
        return <p key={bi} className="gt-p">{inline(block)}</p>;
      })}
    </>
  );
}

export default function GuidedTour() {
  const {
    active, tour, step, stepIndex, total, isSimulating, isAuto, playing, speed,
    serviceId, companyId, next, prev, goTo, pause, trackService, trackRelation, resolveServiceByName, setPlaying, setSpeed, switchToManual,
  } = useTutorial();
  const navigate = useNavigate();
  const location = useLocation();

  const [rect, setRect] = useState(null);
  const [missing, setMissing] = useState(false);
  const [done, setDone] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [teacher, setTeacher] = useState(() => window.localStorage.getItem('cmdb-tour-teacher') === '1');
  const [pulse, setPulse] = useState(null);   // onda en el punto donde se ha pulsado
  const [cursor, setCursor] = useState(null);  // posición del ratón que se mueve solo
  const [notice, setNotice] = useState('');   // lo que acaba de hacer la demo
  const [autoError, setAutoError] = useState('');
  const [retry, setRetry] = useState(0);      // reintento manual de un paso que falló

  const ctx = useMemo(() => createCtx(), []);
  const baselineRef = useRef({});     // { stepId: valor de referencia }
  const measuredRef = useRef({});     // { stepId: { value, since } }
  const advancedRef = useRef(null);   // evita avanzar dos veces el mismo paso
  const scrolledRef = useRef({});     // pasos en los que ya se acercó el elemento

  useEffect(() => {
    window.localStorage.setItem('cmdb-tour-teacher', teacher ? '1' : '0');
  }, [teacher]);

  // La onda del clic se borra sola: marca el gesto, no se queda en pantalla.
  useEffect(() => {
    if (!pulse) return undefined;
    const id = setTimeout(() => setPulse(null), 650);
    return () => clearTimeout(id);
  }, [pulse]);

  // ── 0. Guiones que trabajan sobre un servicio que YA existe ──
  // Declaran `serviceRef` (nombre) en vez de crearlo, así que hay que resolver
  // su id antes del primer paso que necesite la ruta del Diseñador.
  const resolvedRef = useRef(null);
  useEffect(() => {
    if (!active || !tour?.serviceRef || serviceId) return;
    if (resolvedRef.current === tour.id) return;
    resolvedRef.current = tour.id;
    resolveServiceByName(tour.serviceRef).then(id => {
      if (!id) setNotice(`No encuentro «${tour.serviceRef}» en el inventario; sigue el paso a mano.`);
    });
  }, [active, tour, serviceId, resolveServiceByName]);

  // ── 1. Llevar al usuario a la pantalla del paso ──
  useEffect(() => {
    if (!active || !step) return;
    // OJO: el `serviceId` que necesita la ruta es el del servicio creado por el
    // tutorial (estado), no el del guion. Confundirlos abría el Diseñador sin
    // el servicio cargado: la página de lista en vez del lienzo.
    const route = typeof step.route === 'function' ? step.route({ serviceId }) : step.route;
    if (!route) return;
    const target = route.split('?')[0];
    if (location.pathname === target) return;

    // En la demo se navega como una persona: el ratón va al menú lateral, abre
    // el grupo si hace falta y pulsa la entrada. Si la ruta lleva parámetros
    // (el Diseñador de un servicio concreto), no hay entrada de menú que valga:
    // se navega directo.
    if (isAuto && playing && !route.includes('?') && step.viaMenu !== false) {
      setNotice('Yendo por el menú…');
      goViaMenu(route, {
        onMove: setCursor,
        onPulse: setPulse,
        speed,
        navigate,
        say: setNotice,
      }).then(() => setNotice(''));
      return;
    }
    navigate(route);
  }, [active, step, navigate, location.pathname, serviceId, isAuto, playing, speed]);

  // ── 2. Seguir al elemento resaltado ──
  const measure = useCallback(() => {
    if (!step) return;
    if (!step.target) { setRect(null); setMissing(false); return; }
    const el = findTarget(step.target);
    if (!el) { setMissing(true); setRect(null); return; }
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return;   // todavía sin pintar
    setMissing(false);
    const next = { left: r.left, top: r.top, width: r.width, height: r.height, right: r.right, bottom: r.bottom };
    // Solo se actualiza si ha cambiado: si no, cada medición reiniciaría la
    // transición del foco y la página parecería "viva" (y no se puede pulsar).
    setRect(prev => (
      prev && Math.abs(prev.left - next.left) < 0.5 && Math.abs(prev.top - next.top) < 0.5 &&
      Math.abs(prev.width - next.width) < 0.5 && Math.abs(prev.height - next.height) < 0.5
        ? prev
        : next
    ));
  }, [step]);

  useEffect(() => {
    if (!active || !step || minimized) return undefined;
    measure();
    const kick = [80, 250, 600, 1200].map(ms => setTimeout(measure, ms));
    const onMove = () => measure();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    const id = setInterval(measure, POLL_MS);
    return () => {
      kick.forEach(clearTimeout);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
      clearInterval(id);
    };
  }, [active, step, measure, location.pathname, minimized]);

  // Si el elemento está fuera de la vista, se acerca al usuario UNA vez por
  // paso. Si se repitiese en cada medición, la página no dejaría de moverse y
  // ni se podría pulsar ni escribir (el elemento nunca queda quieto).
  useEffect(() => {
    if (!active || !step?.target || !rect) return;
    if (scrolledRef.current[step.id]) return;
    const off = rect.top < 90 || rect.bottom > window.innerHeight - 40;
    if (!off) return;
    scrolledRef.current[step.id] = true;
    const el = findTarget(step.target);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, location.pathname, rect]);

  // ── 4. Modo demo: el tutorial hace los pasos solo ──
  // El estado "en reproducción" se lleva en refs a propósito: cuando algo
  // cambia a mitad de un paso (el servicio recién creado cambia de id, por
  // ejemplo) React vuelve a lanzar el efecto, y si se cancelase la ejecución en
  // curso el paso se quedaría colgado sin avanzar nunca.
  const runningRef = useRef(null);
  const playingRef = useRef(true);
  const retriedRef = useRef(0);
  const knownRelRef = useRef(new Set());   // relaciones vistas, para saber cuáles son nuevas
  playingRef.current = playing;

  useEffect(() => {
    if (!active || !step || !isAuto || !playing) return;
    // El botón «Continuar» tras un fallo reintenta el paso actual una vez.
    if (retriedRef.current !== retry) {
      retriedRef.current = retry;
      runningRef.current = null;
    }
    if (runningRef.current === step.id) return;   // este paso ya se está haciendo
    runningRef.current = step.id;
    setAutoError('');
    setNotice('');

    const run = async () => {
      await sleep(500 / speed);
      if (!playingRef.current) return;            // pausado: no se encadena

      if (step.auto?.length) {
        const res = await performStep(step, {
          companyId,
          serviceId,
          ctx,
          speed,
          onPulse: setPulse,
          onMove: setCursor,
          say: setNotice,
          onServiceId: (id, name) => trackService(id, name),
        });
        if (!playingRef.current) return;
        if (!res.ok) {
          // Mejor parar y contarlo que seguir haciendo cosas a medias.
          setAutoError(`No he podido hacer este paso solo (${res.error}). Pulsa «Continuar» para reintentarlo, o «Lo hago yo» y sigue tú.`);
          setPlaying(false);
          return;
        }
        // Las relaciones que se acaban de crear quedan apuntadas: el deshacer
        // del final también tiene que poder quitarlas.
        if (step.auto.some(a => a.relationModal) && serviceId && companyId) {
          try {
            const deps = await api.getServiceDependencies(serviceId, companyId);
            const ids = [...(deps.relations_out || []), ...(deps.relations_in || [])].map(r => r.id);
            const nuevas = ids.filter(id => !knownRelRef.current.has(id));
            ids.forEach(id => knownRelRef.current.add(id));
            nuevas.forEach(id => trackRelation(id));
            if (nuevas.length) setNotice(`${nuevas.length} relación(es) guardada(s)`);
          } catch { /* si falla, el deshacer simplemente no las tocará */ }
        }
      } else {
        // Paso que debe hacer la persona (elegir el equipo, dibujar la
        // topología...): se explica y se deja tiempo para leerlo.
        if (step.autoNote) setNotice(step.autoNote);
        await sleep(2400 / speed);
        if (!playingRef.current) return;
      }

      setDone(true);
      await sleep(800 / speed);
      if (!playingRef.current) return;
      if (stepIndex >= total - 1) pause();
      else next();
    };

    run();
  }, [active, step, isAuto, playing, speed, ctx, serviceId, companyId, stepIndex, total, next, pause, setPlaying, trackService, trackRelation, retry]);

  // ── 5. Detectar que el usuario ha hecho el paso (modos manuales) ──
  useEffect(() => {
    if (!active || !step || isAuto) return undefined;
    setDone(false);
    advancedRef.current = null;

    const evaluate = () => {
      if (!step.detect) return false;
      const baseline = baselineRef.current[step.id];
      if (step.measure && baseline === undefined) return false;   // aún sin referencia
      return Boolean(step.detect(ctx, { ...step, baseline: baseline ?? 0 }));
    };

    // Referencia para los pasos que miden cantidades (flechas del lienzo...).
    const takeBaseline = () => {
      if (!step.measure || baselineRef.current[step.id] !== undefined) return;
      const value = Number(step.measure(ctx)) || 0;
      const prev = measuredRef.current[step.id];
      if (prev && prev.value === value && Date.now() - prev.since > STABLE_MS) {
        baselineRef.current[step.id] = value;
      } else if (!prev || prev.value !== value) {
        measuredRef.current[step.id] = { value, since: Date.now() };
      }
    };

    const id = setInterval(() => {
      takeBaseline();
      if (evaluate()) {
        setDone(true);
        if (step.advance === 'auto' && advancedRef.current !== step.id) {
          advancedRef.current = step.id;
          setTimeout(() => next(), 900);
        }
      }
    }, POLL_MS);

    return () => clearInterval(id);
  }, [active, step, ctx, next]);

  // El servicio recién creado se recuerda para poder deshacerlo al final.
  useEffect(() => {
    if (!active || !step || !done || !step.trackServiceName) return;
    const id = ctx.serviceRowId(step.trackServiceName);
    if (id) trackService(id, step.trackServiceName);
  }, [active, step, done, ctx, trackService]);

  // ── Teclado ──
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') pause();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, next, prev, pause]);

  if (!active || !step) return null;

  const isLast = stepIndex >= total - 1;
  const canSkipAction = isSimulating && step.writes;
  const waiting = Boolean(step.detect) && step.advance === 'auto' && !done && !canSkipAction;

  const bubbleStyle = (() => {
    if (minimized) return { left: '50%', bottom: 24, transform: 'translateX(-50%)' };
    if (!rect) return { left: '50%', top: 96, transform: 'translateX(-50%)' };
    return placeBubble(rect, step.placement);
  })();

  const spotStyle = rect
    ? {
      left: rect.left - SPOT_PAD,
      top: rect.top - SPOT_PAD,
      width: rect.width + SPOT_PAD * 2,
      height: rect.height + SPOT_PAD * 2,
    }
    : null;

  return (
    <div className="gt-root" aria-live="polite">
      {!minimized && (
        <div className="gt-veil">
          {spotStyle && <div className="gt-spot" style={spotStyle} />}
        </div>
      )}

      {/* Onda donde acaba de pulsar la demo: se ve EL CLIC, no solo el efecto. */}
      {pulse && (
        <span className="gt-pulse" style={{ left: pulse.x, top: pulse.y }} />
      )}

      {/* Ratón que se mueve solo: viaja hasta el elemento antes de pulsarlo. */}
      {isAuto && playing && cursor && (
        <span className="gt-cursor" style={{ transform: `translate3d(${cursor.x}px, ${cursor.y}px, 0)` }} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path d="M5 2 L5 20 L9.5 15.6 L12.6 22 L15.4 20.7 L12.3 14.4 L18.5 14.2 Z"
              fill="#ffffff" stroke="#4c1d95" strokeWidth="1.4" strokeLinejoin="round" />
          </svg>
        </span>
      )}

      {minimized ? (
        <button type="button" className="gt-restore" onClick={() => setMinimized(false)}>
          <span className="gt-restore-icon">{tour.icon}</span>
          Seguir el tutorial · paso {stepIndex + 1}/{total}
        </button>
      ) : isAuto ? (
        /* Modo demo: panel ancho abajo. Deja libre toda la zona central, que es
           donde está la información que hay que mirar. */
        <div className="gt-dock" role="dialog" aria-label={`Tutorial: ${step.title}`}>
          <div className="gt-dock-bar"><span style={{ width: `${((stepIndex + 1) / total) * 100}%` }} /></div>
          <div className="gt-dock-inner">
            <div className="gt-dock-left">
              <div className="gt-dock-head">
                <span className="gt-badge">{tour.icon} {tour.title}</span>
                <span className="gt-counter">paso {stepIndex + 1} de {total}</span>
                {playing ? <span className="gt-live">● en directo</span> : <span className="gt-paused">❚❚ en pausa</span>}
              </div>
              <h3 className="gt-title">{step.title}</h3>
              <div className="gt-body gt-body--dock"><RichText text={step.body} /></div>
              {notice && <div className="gt-doing">↳ {notice}</div>}
              {autoError && <div className="gt-note gt-note--warn">{autoError}</div>}
              {!playing && !autoError && (
                <div className="gt-note">
                  En pausa. La demo seguirá cuando le des al play (o hazlo tú paso a paso).
                </div>
              )}
            </div>
            <div className="gt-dock-right">
              <button
                type="button"
                className="gt-btn gt-btn--primary gt-play"
                onClick={() => { setPlaying(!playing); setRetry(r => r + 1); }}
                title={playing ? 'Pausar la demo' : 'Continuar la demo'}
              >
                {playing ? '❚❚ Pausar' : '▶ Continuar'}
              </button>
              <button type="button" className="gt-btn gt-btn--ghost" onClick={() => setSpeed(speed === 1 ? 2 : 1)}>
                {speed === 1 ? '⏩ x2' : '⏪ x1'}
              </button>
              <button type="button" className="gt-btn gt-btn--ghost" onClick={() => goTo(stepIndex + 1)} disabled={isLast}>
                Paso siguiente
              </button>
              <button type="button" className="gt-btn gt-btn--ghost" onClick={switchToManual} title="Deja la demo y sigue tú">
                ✋ Lo hago yo
              </button>
              <button type="button" className="gt-btn gt-btn--ghost" onClick={pause}>Salir</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="gt-bubble" style={bubbleStyle} role="dialog" aria-label={`Tutorial: ${step.title}`}>
          <div className="gt-bubble-head">
            <span className="gt-badge">{tour.icon} {tour.title}</span>
            <span className="gt-counter">{stepIndex + 1} / {total}</span>
          </div>

          <div className="gt-progress"><span style={{ width: `${((stepIndex + 1) / total) * 100}%` }} /></div>

          <h3 className="gt-title">{step.title}</h3>
          <div className="gt-body"><RichText text={step.body} /></div>

          {teacher && step.tip && (
            <div className="gt-tip">
              <span className="gt-tip-flag">Profesor</span>
              <span>{step.tip}</span>
            </div>
          )}

          {step.autoNote && (
            <div className="gt-note gt-note--soft">{step.autoNote}</div>
          )}

          {canSkipAction && (
            <div className="gt-note">
              Modo simulación: este paso escribiría en la base de datos, así que no se ejecuta de verdad.
            </div>
          )}

          {missing && (
            <div className="gt-note gt-note--warn">
              No encuentro el elemento que quiero enseñarte (¿hay otra ventana abierta encima?). Puedes continuar igualmente.
            </div>
          )}

          <div className="gt-foot">
            <div className="gt-foot-left">
              <button type="button" className="gt-btn gt-btn--ghost" onClick={() => setMinimized(true)}>Minimizar</button>
              <button
                type="button"
                className={`gt-btn gt-btn--ghost ${teacher ? 'is-on' : ''}`}
                onClick={() => setTeacher(v => !v)}
                title="Notas de profesor: explicaciones extra en cada paso"
              >
                👩‍🏫 Profesor
              </button>
            </div>
            <div className="gt-foot-right">
              <button type="button" className="gt-btn gt-btn--ghost" onClick={pause} title="Pausa el tutorial y vuelve cuando quieras">Pausar y salir</button>
              <button type="button" className="gt-btn gt-btn--ghost" onClick={prev} disabled={stepIndex === 0}>Atrás</button>
              {!isLast && (
                <button type="button" className="gt-btn gt-btn--ghost" onClick={() => goTo(stepIndex + 1)}>Saltar</button>
              )}
              <button
                type="button"
                className={`gt-btn ${done ? 'gt-btn--ok' : 'gt-btn--primary'}`}
                onClick={isLast ? pause : next}
              >
                {isLast ? 'Terminar' : done || canSkipAction ? 'Siguiente →' : 'Ya lo he hecho'}
              </button>
            </div>
          </div>

          <div className="gt-status">
            {done && !isLast && <span className="gt-status-ok">✓ Hecho, avanzando…</span>}
            {waiting && <span className="gt-status-wait">Cuando lo hagas, sigo solo · también puedes avanzar a mano</span>}
          </div>
        </div>
      )}
    </div>
  );
}
